import * as React from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import { useLanguage } from "../../../contexts/LanguageContext";
import { vitalsColors, rolesFor, hexToRGBA } from "../../../common/theme/brandTokens";

type ForecastPoint = {
    time_idx: number;
    value: number;
    pred_interval_lo: number;
    pred_interval_hi: number;
};

type Props = {
    charttime: string[];
    systolic_blood_pressure: (number | null)[];
    diastolic_blood_pressure: (number | null)[];
    heart_rate: (number | null)[];
    o2_saturation: (number | null)[];
    forecasts?: {
        systolic_blood_pressure: ForecastPoint[];
        diastolic_blood_pressure: ForecastPoint[];
        heart_rate: ForecastPoint[];
        o2_saturation: ForecastPoint[];
    };
    /** Height per panel in pixels (default 100). Total height = panels × panelHeight. */
    height?: number;
};

type VitalKey = "SBP" | "DBP" | "HR" | "SpO2";

function toTimeLabel(d: Date) {
    const hh = d.getHours().toString().padStart(2, "0");
    const mm = d.getMinutes().toString().padStart(2, "0");
    return `${hh}:${mm}`;
}

function numOrNull(x: number | null | undefined): number | null {
    return typeof x === "number" && Number.isFinite(x) ? x : null;
}

/**
 * Y-axis ticks at multiples of 20, always exactly 5 ticks (80-unit span).
 * The window is centred on the data midpoint so the series sits in the
 * middle of each panel. Expands symmetrically (20 units each side) if the
 * data range exceeds the initial window.
 */
function snapDomain(vals: number[]): { lo: number; hi: number; ticks: number[] } | null {
    if (!vals.length) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const center = (min + max) / 2;
    // Start with an 80-unit symmetric window (5 ticks) centred on the midpoint
    let lo = Math.round((center - 40) / 20) * 20;
    let hi = lo + 80;
    // Expand each side independently until the window covers all data
    while (lo > min) lo -= 20;
    while (hi < max) hi += 20;
    const ticks: number[] = [];
    for (let t = lo; t <= hi; t += 20) ticks.push(t);
    return { lo, hi, ticks };
}

/**
 * SpO2-specific Y-axis domain: hi is always 100, lo is derived from the
 * data minimum (snapped down to the nearest even multiple of 2).
 */
function snapDomainSpO2(vals: number[]): { lo: number; hi: number; ticks: number[] } | null {
    if (!vals.length) return null;
    const min = Math.min(...vals);
    // hi is always 100; lo snaps DOWN to the nearest multiple of 20 below the
    // data minimum — consistent with the 20-unit tick convention used elsewhere.
    let lo = Math.floor(min / 20) * 20;
    if (lo >= 100) lo = 80; // safety: always at least one tick below 100
    const hi = 100;
    const ticks: number[] = [];
    for (let t = lo; t <= hi; t += 20) ticks.push(t);
    return { lo, hi, ticks };
}

// VitalTooltip reads ONLY from row._tooltip — a plain object stored on each data row
// under a key that no recharts dataKey ever references. recharts only processes
// keys that match dataKey props on chart elements (SBP, SBP_f, SBP_ci_lo,
// SBP_ci_range). _tooltip is completely invisible to recharts.
function VitalTooltip({
    active,
    payload,
    vitalKey,
    unit,
    label,
    isDark,
}: {
    active?: boolean;
    payload?: any[];
    vitalKey: VitalKey;
    unit: string;
    label: string;
    isDark: boolean;
}) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;

    // All raw values are stored in _tooltip by the parent component. recharts never
    // modifies this field since no chart element has dataKey="_tooltip".
    const t = row._tooltip as {
        SBP?: number | null; SBP_f?: number | null; SBP_ci_lo?: number | null; SBP_ci_hi?: number | null;
        DBP?: number | null; DBP_f?: number | null; DBP_ci_lo?: number | null; DBP_ci_hi?: number | null;
        HR?: number | null; HR_f?: number | null; HR_ci_lo?: number | null; HR_ci_hi?: number | null;
        SpO2?: number | null; SpO2_f?: number | null; SpO2_ci_lo?: number | null; SpO2_ci_hi?: number | null;
        timeStr: string; isForecast: boolean;
    } | undefined;

    // Fallback diagnostic if _tooltip is missing (should not happen).
    if (!t) {
        console.warn('[VitalTooltip] _tooltip missing on row. Raw payload[0].payload:', row);
        return null;
    }

    const histVal = t[vitalKey] ?? null;
    const fcVal = t[`${vitalKey}_f` as keyof typeof t] as number | null ?? null;
    const ciLo = t[`${vitalKey}_ci_lo` as keyof typeof t] as number | null ?? null;
    const ciHi = t[`${vitalKey}_ci_hi` as keyof typeof t] as number | null ?? null;

    // Prefer forecast value when present (junction + forecast rows).
    const displayVal = fcVal ?? histVal;

    if (displayVal == null) return null;

    const r = rolesFor(isDark);
    const bg = hexToRGBA(r.elevated, 0.96);
    const border = `1px solid ${r.divider}`;
    const color = r.text;
    const hasCi = ciLo != null && ciHi != null && ciHi > ciLo;

    return (
        <div style={{ fontSize: 9, padding: "3px 7px", borderRadius: 4, lineHeight: 1.5,
                      backgroundColor: bg, border, color, minWidth: 0 }}>
            <div style={{ marginBottom: 1, fontWeight: 500 }}>
                {t.isForecast ? `${t.timeStr} (forecast)` : t.timeStr}
            </div>
            <div>{`${label}: ${Math.round(displayVal)} ${unit}`}</div>
            {hasCi && <div>{`CI: ${Math.round(ciLo!)}–${Math.round(ciHi!)} ${unit}`}</div>}
        </div>
    );
}

interface PanelProps {
    data: any[];
    vitalKey: VitalKey;
    label: string;
    unit: string;
    color: string;
    showXAxis: boolean;
    tickInterval: number;
    panelHeight: number;
    hasForecast: boolean;
    syncId: string;
    isFirst: boolean;
    isSpO2?: boolean;
    gridColor?: string;
    tickFill?: string;
    isDark?: boolean;
}

function VitalPanel({
    data,
    vitalKey,
    label,
    unit,
    color,
    showXAxis,
    tickInterval,
    panelHeight,
    hasForecast,
    syncId,
    isFirst,
    isSpO2,
    gridColor = rolesFor(false).divider,
    tickFill = rolesFor(false).text3,
    isDark = false,
}: PanelProps) {
    const r = rolesFor(isDark);
    const fcKey = `${vitalKey}_f`;
    // CI stored as two stacked Areas: transparent base (_ci_lo) + filled range (_ci_range).
    const ciLoKey = `${vitalKey}_ci_lo`;
    const ciRangeKey = `${vitalKey}_ci_range`;

    // Domain is driven only by the actual line data (historical + forecast centre).
    // CI bounds are excluded so they don't inflate the scale; they render with
    // allowDataOverflow={true} and may visually extend slightly outside the domain.
    const vals: number[] = [];
    for (const row of data) {
        const v = row[vitalKey];
        if (typeof v === "number" && Number.isFinite(v)) vals.push(v);
        const f = row[fcKey];
        if (typeof f === "number" && Number.isFinite(f)) vals.push(f);
    }

    const dom = isSpO2 ? snapDomainSpO2(vals) : snapDomain(vals);
    return (
        <div style={{ width: "100%", position: "relative", marginTop: isFirst ? 0 : -5 }}>
            <ResponsiveContainer width="100%" height={panelHeight}>
                <ComposedChart
                    data={data}
                    syncId={syncId}
                    margin={{ top: 5, right: 8, bottom: showXAxis ? 8 : 0, left: 4 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                    <YAxis
                        type="number"
                        width={32}
                        interval={0}
                        tick={(tickProps: any) => {
                            const { x, y, payload } = tickProps;
                            return (
                                <text
                                    x={x}
                                    y={y}
                                    dy={0}
                                    textAnchor="end"
                                    fill={tickFill}
                                    fontSize={8}
                                    dominantBaseline="central"
                                >
                                    {payload.value}
                                </text>
                            );
                        }}
                        domain={dom ? [dom.lo, dom.hi] : ["auto", "auto"]}
                        ticks={dom ? dom.ticks : undefined}
                        allowDecimals={false}
                        allowDataOverflow={true}
                        tickLine={{ stroke: r.divider, strokeWidth: 1 }}
                        axisLine={false}
                    />

                    <XAxis
                        dataKey="timeLabel"
                        tick={showXAxis ? { fontSize: 7 } : false}
                        axisLine={showXAxis}
                        tickLine={showXAxis}
                        interval={tickInterval}
                        tickMargin={2}
                        height={showXAxis ? 18 : 0}
                        padding={{ left: 0, right: 0 }}
                    />

                    <Tooltip
                        content={(props: any) => (
                            <VitalTooltip
                                active={props.active}
                                payload={props.payload}
                                vitalKey={vitalKey}
                                unit={unit}
                                label={label}
                                isDark={isDark ?? false}
                            />
                        )}
                        wrapperStyle={{ zIndex: 9999 }}
                    />
                    {hasForecast && (
                        <>
                            {/* Stacked Areas: transparent base lifts the fill to ci_lo, 
                                then the range fill covers ci_lo→ci_hi */}
                            <Area
                                type="linear"
                                dataKey={ciLoKey}
                                stackId={`ci_${vitalKey}`}
                                fill="transparent"
                                stroke="none"
                                isAnimationActive={false}
                                legendType="none"
                                name={undefined}
                                dot={false}
                                activeDot={false}
                            />
                            <Area
                                type="linear"
                                dataKey={ciRangeKey}
                                stackId={`ci_${vitalKey}`}
                                fill={color}
                                fillOpacity={0.2}
                                stroke="none"
                                isAnimationActive={false}
                                legendType="none"
                                name={undefined}
                                dot={false}
                                activeDot={false}
                            />
                        </>
                    )}
                    <Line
                        type="linear"
                        dataKey={vitalKey}
                        name={label}
                        stroke={color}
                        strokeWidth={1.4}
                        dot={{ r: 2, fill: color, strokeWidth: 0 }}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                        connectNulls
                    />

                    {/* Forecast line — dashed, with dots to mark each data point */}
                    {hasForecast && (
                        <Line
                            type="linear"
                            dataKey={fcKey}
                            name={`${label} (fc)`}
                            stroke={color}
                            strokeWidth={1.4}
                            strokeDasharray="5 3"
                            dot={{ r: 2, fill: color, strokeWidth: 0 }}
                            activeDot={{ r: 4 }}
                            isAnimationActive={false}
                            connectNulls
                            legendType="none"
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

// ── Shared vital colors / panel configs ─────────────────────────────────────────
// Colors are defined once here and reused by every panel, the card list in the
// Patient page, and the (legacy) stacked chart. HR flips with the theme.
export function getVitalColors(_isDark: boolean): Record<VitalKey, string> {
    return {
        SBP: vitalsColors.SBP,
        DBP: vitalsColors.DBP,
        HR: vitalsColors.HR,
        SpO2: vitalsColors.SpO2,
    };
}

export type VitalPanelConfig = { key: VitalKey; label: string; unit: string; color: string };

/** Hook: the four panel configs (label/unit/color), themed. */
export function useVitalPanelConfigs(): VitalPanelConfig[] {
    const { t } = useLanguage();
    const muiTheme = useTheme();
    const colors = getVitalColors(muiTheme.palette.mode === 'dark');
    return [
        { key: "SBP", label: t('vitals.systolicBP'), unit: "mmHg", color: colors.SBP },
        { key: "DBP", label: t('vitals.diastolicBP'), unit: "mmHg", color: colors.DBP },
        { key: "HR", label: t('vitals.heartRate'), unit: "bpm", color: colors.HR },
        { key: "SpO2", label: t('vitals.spo2'), unit: "%", color: colors.SpO2 },
    ];
}

// ── Data builder ────────────────────────────────────────────────────────────────
// Builds the shared data rows (historical + forecast/junction/CI) once. Pure and
// theme-independent, so a parent can build it a single time and feed it to every
// SingleVitalChart panel. Returns null when there is no data.
export type VitalsData = {
    data: any[];
    hasForecast: boolean;
    tickInterval: number;
    hasData: Record<VitalKey, boolean>;
};

export function buildVitalsData({
    charttime,
    systolic_blood_pressure,
    diastolic_blood_pressure,
    heart_rate,
    o2_saturation,
    forecasts,
}: Omit<Props, "height">): VitalsData | null {
    const n = charttime.length;
    if (n === 0) return null;

    const safeNum = (arr: (number | null)[], i: number): number | null =>
        i < arr.length ? numOrNull(arr[i]) : null;

    const historicalDates = Array.from({ length: n }).map((_, i) => new Date(charttime[i]));

    // ── Build shared data array ─────────────────────────────────────────────
    const data: any[] = historicalDates.map((d, i) => ({
        t: d,
        timeLabel: toTimeLabel(d),
        SBP: safeNum(systolic_blood_pressure, i),
        DBP: safeNum(diastolic_blood_pressure, i),
        HR: safeNum(heart_rate, i),
        SpO2: safeNum(o2_saturation, i),
        // Forecast columns — null on historical rows
        SBP_f: null, DBP_f: null, HR_f: null, SpO2_f: null,
        // Stacked CI columns — null on historical rows
        SBP_ci_lo: null, SBP_ci_range: null,
        DBP_ci_lo: null, DBP_ci_range: null,
        HR_ci_lo: null, HR_ci_range: null,
        SpO2_ci_lo: null, SpO2_ci_range: null,
        // _tooltip: raw values recharts will never touch (no dataKey points here)
        _tooltip: {
            SBP: safeNum(systolic_blood_pressure, i),
            DBP: safeNum(diastolic_blood_pressure, i),
            HR: safeNum(heart_rate, i),
            SpO2: safeNum(o2_saturation, i),
            SBP_f: null, DBP_f: null, HR_f: null, SpO2_f: null,
            SBP_ci_lo: null, SBP_ci_hi: null,
            DBP_ci_lo: null, DBP_ci_hi: null,
            HR_ci_lo: null, HR_ci_hi: null,
            SpO2_ci_lo: null, SpO2_ci_hi: null,
            timeStr: toTimeLabel(d),
            isForecast: false,
        },
    }));

    // ── Append forecast projection rows ────────────────────────────────────
    const hasForecast =
        !!forecasts &&
        (forecasts.systolic_blood_pressure.length > 0 ||
            forecasts.diastolic_blood_pressure.length > 0 ||
            forecasts.heart_rate.length > 0 ||
            forecasts.o2_saturation.length > 0);

    if (hasForecast && forecasts) {
        const lastDate = historicalDates[n - 1];
        const intervalMs =
            n >= 2 ? lastDate.getTime() - historicalDates[n - 2].getTime() : 3600_000;

        // Junction: seed the dashed forecast line AND the CI band from the last
        // historical point. CI starts at zero width (lo = value, range = 0) so
        // the shaded area fans out from a single point into the forecast region.
        const junction = data[n - 1];
        junction.SBP_f = junction.SBP;
        junction.DBP_f = junction.DBP;
        junction.HR_f = junction.HR;
        junction.SpO2_f = junction.SpO2;
        // Zero-width CI seed — stacked base = value, range = 0
        if (junction.SBP != null) { junction.SBP_ci_lo = junction.SBP; junction.SBP_ci_range = 0; }
        if (junction.DBP != null) { junction.DBP_ci_lo = junction.DBP; junction.DBP_ci_range = 0; }
        if (junction.HR != null) { junction.HR_ci_lo = junction.HR; junction.HR_ci_range = 0; }
        if (junction.SpO2 != null) { junction.SpO2_ci_lo = junction.SpO2; junction.SpO2_ci_range = 0; }
        // Mirror into _tooltip
        junction._tooltip.SBP_f = junction.SBP;
        junction._tooltip.DBP_f = junction.DBP;
        junction._tooltip.HR_f = junction.HR;
        junction._tooltip.SpO2_f = junction.SpO2;
        junction._tooltip.isForecast = false; // junction = still a real data point

        const fcMap: Record<VitalKey, Record<number, ForecastPoint>> = {
            SBP: {}, DBP: {}, HR: {}, SpO2: {},
        };
        forecasts.systolic_blood_pressure.forEach((p) => { fcMap.SBP[p.time_idx] = p; });
        forecasts.diastolic_blood_pressure.forEach((p) => { fcMap.DBP[p.time_idx] = p; });
        forecasts.heart_rate.forEach((p) => { fcMap.HR[p.time_idx] = p; });
        forecasts.o2_saturation.forEach((p) => { fcMap.SpO2[p.time_idx] = p; });

        const allIdxs = Array.from(
            new Set([
                ...forecasts.systolic_blood_pressure.map((p) => p.time_idx),
                ...forecasts.diastolic_blood_pressure.map((p) => p.time_idx),
                ...forecasts.heart_rate.map((p) => p.time_idx),
                ...forecasts.o2_saturation.map((p) => p.time_idx),
            ])
        ).sort((a, b) => a - b);

        for (const idx of allIdxs) {
            const projectedDate = new Date(lastDate.getTime() + idx * intervalMs);

            const row: any = {
                t: projectedDate,
                timeLabel: toTimeLabel(projectedDate),
                SBP: null, DBP: null, HR: null, SpO2: null,
                SBP_f: null, DBP_f: null, HR_f: null, SpO2_f: null,
                SBP_ci_lo: null, SBP_ci_range: null,
                DBP_ci_lo: null, DBP_ci_range: null,
                HR_ci_lo: null, HR_ci_range: null,
                SpO2_ci_lo: null, SpO2_ci_range: null,
                _tooltip: {
                    SBP: null, DBP: null, HR: null, SpO2: null,
                    SBP_f: null, DBP_f: null, HR_f: null, SpO2_f: null,
                    SBP_ci_lo: null, SBP_ci_hi: null,
                    DBP_ci_lo: null, DBP_ci_hi: null,
                    HR_ci_lo: null, HR_ci_hi: null,
                    SpO2_ci_lo: null, SpO2_ci_hi: null,
                    timeStr: toTimeLabel(projectedDate),
                    isForecast: true,
                },
            };

            for (const [vk, fk, loK, rngK, ciLoT, ciHiT] of [
                ["SBP", "SBP_f", "SBP_ci_lo", "SBP_ci_range", "SBP_ci_lo", "SBP_ci_hi"],
                ["DBP", "DBP_f", "DBP_ci_lo", "DBP_ci_range", "DBP_ci_lo", "DBP_ci_hi"],
                ["HR", "HR_f", "HR_ci_lo", "HR_ci_range", "HR_ci_lo", "HR_ci_hi"],
                ["SpO2", "SpO2_f", "SpO2_ci_lo", "SpO2_ci_range", "SpO2_ci_lo", "SpO2_ci_hi"],
            ] as const) {
                const fc = fcMap[vk][idx];
                if (fc) {
                    // Chart rendering keys (recharts processes these)
                    row[fk] = fc.value;
                    row[loK] = fc.pred_interval_lo;
                    row[rngK] = fc.pred_interval_hi - fc.pred_interval_lo;
                    // Protected tooltip keys (recharts never touches these)
                    row._tooltip[fk] = fc.value;
                    row._tooltip[ciLoT] = fc.pred_interval_lo;
                    row._tooltip[ciHiT] = fc.pred_interval_hi;
                }
            }

            data.push(row);
        }
    }
    // ── Determine which vitals have any non-null historical values ──────────
    const hasData: Record<VitalKey, boolean> = {
        SBP: systolic_blood_pressure.some((v) => v != null),
        DBP: diastolic_blood_pressure.some((v) => v != null),
        HR: heart_rate.some((v) => v != null),
        SpO2: o2_saturation.some((v) => v != null),
    };

    const tickInterval = Math.max(1, Math.floor(data.length / 6));

    return { data, hasForecast, tickInterval, hasData };
}

// ── Single-vital chart ──────────────────────────────────────────────────────────
// Renders exactly one vital's panel with its own X axis. Meant to sit directly
// below that vital's card; the data array is built once by the parent via
// buildVitalsData and shared across every SingleVitalChart.
export function SingleVitalChart({
    vitalKey,
    data,
    hasForecast,
    tickInterval,
    height = 100,
}: {
    vitalKey: VitalKey;
    data: any[];
    hasForecast: boolean;
    tickInterval: number;
    height?: number;
}) {
    const configs = useVitalPanelConfigs();
    const muiTheme = useTheme();
    const isDark = muiTheme.palette.mode === 'dark';
    const config = configs.find((c) => c.key === vitalKey);
    if (!config) return null;

    return (
        <VitalPanel
            data={data}
            vitalKey={config.key}
            label={config.label}
            unit={config.unit}
            color={config.color}
            showXAxis={true}
            tickInterval={tickInterval}
            panelHeight={height}
            hasForecast={hasForecast}
            syncId="vitals-sync"
            isFirst={true}
            isSpO2={config.key === "SpO2"}
            gridColor={rolesFor(isDark).divider}
            tickFill={rolesFor(isDark).text3}
            isDark={isDark}
        />
    );
}
